import { Fragment, useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { auth } from "../firebase";
import {
  EXPENSE_CATEGORIES, createExpense, approveExpense, rejectExpense, getOperatingExpensesForPeriod, listManualExpenses
} from "../services/financeCore/operatingExpenseService";

const money = (value) => `PKR ${Math.round(Number(value || 0)).toLocaleString("en-PK")}`;
const categoryLabel = (value) => String(value || "").replaceAll("_", " ");
const monthLabel = (period) => {
  const [year, month] = String(period || "").split("-").map(Number);
  if (!year || !month) return period;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
};

// Curated payee lists for categories with a known, recurring set of payees —
// speeds up entry and keeps names spelled consistently. Any other category
// keeps the plain free-text field.
const PAYEE_OPTIONS_BY_CATEGORY = {
  PAYMENT_TRANSFER_ON_DIRECTOR_INSTRUCTIONS: ["Imtiaz Payment"],
  BORROWING_COST_EXPENSE: ["Hamood ur Rehman", "Iqbal (Boss Friend)", "Mehnaz Hassan", "Qari Salman", "Abdul Jabbar"]
};
const CUSTOM_PAYEE = "__custom__";

function emptyForm(period) {
  return {
    accountingPeriod: period,
    expenseDate: `${period}-01`,
    category: EXPENSE_CATEGORIES[0],
    description: "",
    vendor: "",
    note: "",
    referenceNumber: "",
    amountBeforeTax: "",
    taxAmount: "",
    grossAmount: "",
    paymentMethod: ""
  };
}

export default function OperatingExpenses() {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState(() => emptyForm(period));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [payeeCustom, setPayeeCustom] = useState(false);

  const load = async (p) => {
    setLoading(true); setError("");
    try {
      // getOperatingExpensesForPeriod()'s own "manual" field is deliberately
      // APPROVED-only (that's what should count toward the P&L total) — this
      // page's own management table needs the full list, DRAFT included, or
      // a newly recorded expense can never be found here to approve it.
      const [summary, allManual] = await Promise.all([
        getOperatingExpensesForPeriod(p),
        listManualExpenses(p)
      ]);
      setData({ ...summary, manual: allManual });
    } catch (err) {
      setError(err.message || "Could not load operating expenses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period); setForm(emptyForm(period)); }, [period]);

  const update = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.value }));

  const changeCategory = (e) => {
    setPayeeCustom(false);
    setForm((current) => ({ ...current, category: e.target.value, vendor: "" }));
  };

  const changePayee = (e) => {
    if (e.target.value === CUSTOM_PAYEE) {
      setPayeeCustom(true);
      setForm((current) => ({ ...current, vendor: "" }));
    } else {
      setForm((current) => ({ ...current, vendor: e.target.value }));
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setMessage(""); setError("");
    try {
      await createExpense(form);
      setMessage("Expense recorded as DRAFT. Approve it below to include it in Monthly P&L.");
      setForm(emptyForm(period));
      await load(period);
    } catch (err) {
      setError(err.message || "Could not record this expense.");
    }
  };

  const approve = async (id) => {
    setMessage(""); setError("");
    try {
      await approveExpense(id);
      setMessage("Expense approved.");
      await load(period);
    } catch (err) {
      setError(err.message || "Could not approve this expense.");
    }
  };

  const reject = async (entry) => {
    if (entry.status === "APPROVED") {
      const confirmed = window.confirm(
        `${entry.description || entry.category} (${money(entry.grossAmount)}) is already approved and counted in Monthly P&L. ` +
        `Rejecting it now will remove it from the P&L total immediately. Continue?`
      );
      if (!confirmed) return;
    }
    const reason = window.prompt("Reason for rejecting this entry (optional):");
    if (reason === null) return; // cancelled, not confirmed with a blank reason
    setMessage(""); setError("");
    try {
      await rejectExpense(entry.id, reason);
      setMessage("Expense rejected.");
      await load(period);
    } catch (err) {
      setError(err.message || "Could not reject this expense.");
    }
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Management Finance</div>
            <h1 className="page-title">Operating Expenses</h1>
            <p className="page-copy">
              Manual entries need approval before they count toward Monthly P&amp;L. Petty cash and permanent
              payroll are pulled in automatically from their own records — nothing is retyped here.
            </p>
          </div>
          <div className="field pnl-print-hide" style={{ minWidth: 160 }}>
            <label>Period</label>
            <input className="input" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </div>
        </header>

        {(message || error) && <div className={`management-alert pnl-print-hide ${error ? "error" : "success"}`}>{error || message}</div>}

        {data && (
          <section className="management-metrics pnl-print-hide">
            <article className="metric-card"><div className="metric-label">Manual (Approved)</div><div className="metric-value">{money(data.manualTotal)}</div></article>
            <article className="metric-card"><div className="metric-label">Petty Cash</div><div className="metric-value">{money(data.pettyCashTotal)}</div></article>
            <article className="metric-card"><div className="metric-label">Permanent Payroll</div><div className="metric-value">{money(data.payrollTotal)}</div></article>
            <article className="metric-card"><div className="metric-label">Total Operating Expenses</div><div className="metric-value">{money(data.total)}</div></article>
          </section>
        )}

        <section className="panel pnl-print-hide">
          <div className="panel-header"><h2 className="panel-title">Record a Manual Expense</h2></div>
          <form className="panel-body" onSubmit={submit}>
            <div className="form-grid">
              <div className="field"><label>Category</label>
                <select className="input" value={form.category} onChange={changeCategory}>
                  {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c.replaceAll("_", " ")}</option>)}
                </select>
              </div>
              <div className="field"><label>Expense Date</label>
                <input className="input" type="date" value={form.expenseDate} onChange={update("expenseDate")} />
              </div>
              <div className="field"><label>Description</label>
                <input className="input" value={form.description} onChange={update("description")} />
              </div>
              <div className="field"><label>Vendor / Payee</label>
                {PAYEE_OPTIONS_BY_CATEGORY[form.category] && !payeeCustom ? (
                  <select className="input" value={form.vendor} onChange={changePayee}>
                    <option value="">-- Select payee --</option>
                    {PAYEE_OPTIONS_BY_CATEGORY[form.category].map((name) => <option key={name} value={name}>{name}</option>)}
                    <option value={CUSTOM_PAYEE}>Other (type manually)</option>
                  </select>
                ) : (
                  <input className="input" value={form.vendor} onChange={update("vendor")} />
                )}
              </div>
              <div className="field"><label>Note (e.g. payee name)</label>
                <input className="input" value={form.note} onChange={update("note")} placeholder="e.g. Paid to Ahmed Khan on Director's instructions" />
              </div>
              <div className="field"><label>Reference</label>
                <input className="input" value={form.referenceNumber} onChange={update("referenceNumber")} />
              </div>
              <div className="field"><label>Amount Before Tax</label>
                <input className="input" type="number" value={form.amountBeforeTax} onChange={update("amountBeforeTax")} />
              </div>
              <div className="field"><label>Tax Amount</label>
                <input className="input" type="number" value={form.taxAmount} onChange={update("taxAmount")} />
              </div>
              <div className="field"><label>Gross Amount *</label>
                <input className="input" type="number" value={form.grossAmount} onChange={update("grossAmount")} />
              </div>
              <div className="field"><label>Payment Method</label>
                <input className="input" value={form.paymentMethod} onChange={update("paymentMethod")} />
              </div>
            </div>
            <div className="button-row">
              <button className="btn btn-primary" type="submit">Record Expense</button>
            </div>
          </form>
        </section>

        <section className="panel pnl-print-hide">
          <div className="panel-header"><h2 className="panel-title">Manual Expenses — {period}</h2></div>
          <div className="panel-body">
            {loading ? <p className="page-copy">Loading…</p> : (
              <table className="data-table">
                <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Vendor</th><th>Note</th><th>Gross</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {(data?.manual || []).map((e) => (
                    <tr key={e.id}>
                      <td>{e.expenseDate}</td><td>{e.category?.replaceAll("_", " ")}</td><td>{e.description}</td>
                      <td>{e.vendor}</td><td>{e.note}</td><td>{money(e.grossAmount)}</td>
                      <td><span className="badge">{e.status}</span>{e.status === "REJECTED" && e.rejectionReason && <div className="page-copy" style={{ marginTop: 4 }}>{e.rejectionReason}</div>}</td>
                      <td>
                        {e.status === "DRAFT" && <button className="btn btn-success" type="button" onClick={() => approve(e.id)}>Approve</button>}
                        {(e.status === "DRAFT" || e.status === "APPROVED") && <button className="btn btn-danger" type="button" style={{ marginLeft: 6 }} onClick={() => reject(e)}>Reject</button>}
                      </td>
                    </tr>
                  ))}
                  {!data?.manual?.length && <tr><td colSpan={8}>No manual expenses recorded for {period}.</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="panel pnl-print-hide">
          <div className="panel-header"><h2 className="panel-title">Petty Cash (auto-imported) — {period}</h2></div>
          <div className="panel-body">
            <p className="page-copy">
              {data?.pettyCash?.length || 0} exported voucher(s) totaling <strong>{money(data?.pettyCashTotal)}</strong>.
              Full voucher-by-voucher detail lives in the Petty Cash Portal — not repeated here to keep this page short.
            </p>
          </div>
        </section>

        <OperatingExpenseDetailReport period={period} data={data} />
      </main>
    </div>
  );
}

// A clean, printable breakdown finance can hand to the director — every
// approved manual entry with its description/payee/note (so a category like
// "Loan to Director" or "Educational Expense" isn't just a bare total),
// grouped by category, plus Petty Cash and Permanent Payroll as single
// aggregate lines (their own detail lives in their own systems). Modeled on
// Monthly P&L's own letterhead/print pattern for a consistent look.
function OperatingExpenseDetailReport({ period, data }) {
  if (!data) return null;
  const approved = (data.manual || []).filter((e) => e.status === "APPROVED");
  const byCategory = new Map();
  approved.forEach((e) => {
    const key = e.category || "OTHER";
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key).push(e);
  });
  const categories = [...byCategory.keys()].sort();
  const now = new Date();

  return (
    <article className="pnl-report" style={{ marginTop: 24 }}>
      <div className="pnl-letterhead">
        <div className="pnl-letterhead-brand">
          <img src="/alm-logo.png" alt="ALM logo" />
          <div>
            <h2 className="pnl-letterhead-title">ALM Outsourcing Services (Pvt) Ltd.</h2>
            <p className="pnl-letterhead-subtitle">Operating Expenses — Detail Report</p>
          </div>
        </div>
        <div className="pnl-letterhead-meta">
          <strong>{monthLabel(period)}</strong><br />
          {approved.length} approved manual entr{approved.length === 1 ? "y" : "ies"}<br />
          Generated {now.toLocaleDateString("en-GB")} by {auth.currentUser?.email || "—"}
        </div>
      </div>

      <table className="data-table">
        <thead><tr><th>Date</th><th>Description</th><th>Vendor / Payee</th><th>Note</th><th>Reference</th><th>Amount</th></tr></thead>
        <tbody>
          {categories.map((category) => (
            <Fragment key={category}>
              <tr><td colSpan={6} style={{ fontWeight: 700, background: "var(--surface)" }}>{categoryLabel(category)}</td></tr>
              {byCategory.get(category).map((e) => (
                <tr key={e.id}>
                  <td>{e.expenseDate}</td><td>{e.description}</td><td>{e.vendor}</td><td>{e.note}</td><td>{e.referenceNumber}</td><td>{money(e.grossAmount)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700 }}>
                <td colSpan={5} style={{ textAlign: "right" }}>Subtotal — {categoryLabel(category)}</td>
                <td>{money(byCategory.get(category).reduce((sum, e) => sum + Number(e.grossAmount || 0), 0))}</td>
              </tr>
            </Fragment>
          ))}
          {!categories.length && <tr><td colSpan={6}>No approved manual expenses for {period}.</td></tr>}
          <tr style={{ fontWeight: 700 }}><td colSpan={5} style={{ textAlign: "right" }}>Petty Cash (see Petty Cash Portal for voucher detail)</td><td>{money(data.pettyCashTotal)}</td></tr>
          <tr style={{ fontWeight: 700 }}><td colSpan={5} style={{ textAlign: "right" }}>Permanent ALM Payroll (see Payroll Generator for staff detail)</td><td>{money(data.payrollTotal)}</td></tr>
        </tbody>
      </table>

      <div className="pnl-total-row" style={{ marginTop: 12 }}><span>Total Operating Expenses</span><strong>{money(data.total)}</strong></div>

      <div className="pnl-footer pnl-print-hide">
        <span>Confidential — prepared for internal management review only.</span>
        <button type="button" className="btn btn-muted" onClick={() => window.print()}>Print / Save as PDF</button>
      </div>
    </article>
  );
}
